import { sortBy, sum, uniq } from "lodash";

import {
  PAYER_TYPE_INSURER,
  PAYER_TYPE_PATIENT,
  PAYMENT_METHODS
} from "constants/payments";
import {
  FEE_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  SERVICE_PRODUCT_TYPE
} from "constants/sales";
import type { LineItem, Sale } from "services/types/sales.type";
import { roundTo } from "utils/common";

import {
  subTypeOrder,
  typeOrder
} from "../patient-hearing-aids-values/hearing-aids-table.utils";

export const getSortedItems = (items: LineItem[]) => {
  return sortBy(items, [
    (item) =>
      typeOrder.get(Number(item.product?.master_product?.type)) ??
      Number.MAX_SAFE_INTEGER,
    (item) =>
      item.product?.master_product?.subtype
        ? (subTypeOrder.get(item.product?.master_product?.subtype) ??
          Number.MAX_SAFE_INTEGER)
        : Number.MAX_SAFE_INTEGER,
    (item) =>
      item.inventory_product?.ear === "L"
        ? 1
        : item.inventory_product?.ear === "R"
          ? 2
          : Number.MAX_SAFE_INTEGER
  ]);
};

export const getSaleTotals = (sale: Sale) => {
  const items = sale.items as LineItem[];

  const paymentMethods = uniq(
    sale.payments?.map((payment) =>
      PAYMENT_METHODS.getFromCode(payment.method)
    ) || []
  );

  const patientPaymentDetails = (sale.payments || [])
    .filter((payment) => payment.payer_type === PAYER_TYPE_PATIENT)
    .map((payment) => {
      const checkNumber = (payment.check_number ?? "").trim();
      const paymentReference = (payment.payment_reference ?? "").trim();
      const description = (payment.description ?? "").trim();
      const reference = checkNumber || paymentReference;
      const detail = reference
        ? reference.startsWith("#")
          ? reference
          : `#${reference}`
        : description;
      return {
        method: PAYMENT_METHODS.getFromCode(payment.method),
        amount: Number(payment.amount) || 0,
        detail
      };
    });

  const totalInsurancePayments = roundTo(
    sum(
      sale.payments
        ?.filter((payment) => payment.payer_type === PAYER_TYPE_INSURER)
        .map((payment) => Number(payment.amount) || 0) || []
    ),
    2
  );

  const totalPatientPayments = roundTo(
    sum(
      sale.payments
        ?.filter((payment) => payment.payer_type === PAYER_TYPE_PATIENT)
        .map((payment) => Number(payment.amount) || 0) || []
    ),
    2
  );

  const totalDiscounts = roundTo(
    sum(items.map((item) => Number(item.discount) || 0) || []),
    2
  );

  const totalTax = roundTo(
    sum(items.map((item) => Number(item.tax_amount) || 0) || []),
    2
  );

  const totalServices = roundTo(
    sum(
      items
        .filter(
          (item) =>
            item.product?.master_product?.type === SERVICE_PRODUCT_TYPE ||
            item.product?.master_product?.type === FEE_PRODUCT_TYPE
        )
        .map(
          (item) => (Number(item.price) || 0) * (Number(item.quantity) || 1)
        ) || []
    ),
    2
  );

  const totalCostOfHearingAids = roundTo(
    sum(
      items
        .filter(
          (item) =>
            item.product?.master_product?.type === HEARING_AID_PRODUCT_TYPE
        )
        .map(
          (item) => (Number(item.price) || 0) * (Number(item.quantity) || 1)
        ) || []
    ),
    2
  );

  const insurerBalance = Number(sale.insurer_responsibility_balance);
  const amountDueFromInsurance = roundTo(
    Number.isFinite(insurerBalance)
      ? insurerBalance
      : sum(items.map((item) => Number(item.insurer_responsibility) || 0)),
    2
  );

  const patientBalance = Number(sale.patient_responsibility_balance);
  const amountDueFromPatient = roundTo(
    Number.isFinite(patientBalance)
      ? patientBalance
      : sum(items.map((item) => Number(item.patient_responsibility) || 0)),
    2
  );

  return {
    paymentMethods,
    patientPaymentDetails,
    totalDiscounts,
    totalTax,
    totalServices,
    total: Number(sale.total) || 0,
    balance: Number(sale.balance) || 0,
    totalCostOfHearingAids,
    totalInsurancePayments,
    totalPatientPayments,
    amountDueFromInsurance,
    amountDueFromPatient
  };
};
